import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Calculator as CalcIcon, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

export default function Calculator() {
  const [sqft, setSqft] = useState([2000]);
  const [material, setMaterial] = useState("asphalt");
  const [pitch, setPitch] = useState("medium");
  const [tearOff, setTearOff] = useState(true);
  const [estimate, setEstimate] = useState({ min: 0, max: 0 });

  useEffect(() => {
    // Base prices per sqft
    const materialCosts = {
      asphalt: 4.5,
      metal: 9.5,
      flat: 6.5,
    };

    // Pitch multipliers
    const pitchMultipliers = {
      low: 1.0,
      medium: 1.15,
      steep: 1.35,
    };

    const basePrice =
      materialCosts[material as keyof typeof materialCosts] || 4.5;
    const pitchMult =
      pitchMultipliers[pitch as keyof typeof pitchMultipliers] || 1.15;
    const tearOffCost = tearOff ? 1.25 : 0;

    const totalPerSqft = basePrice * pitchMult + tearOffCost;
    const totalCost = sqft[0] * totalPerSqft;

    // Provide a range (+/- 15%)
    setEstimate({
      min: Math.round(totalCost * 0.85),
      max: Math.round(totalCost * 1.15),
    });
  }, [sqft, material, pitch, tearOff]);

  return (
    <Card className="w-full max-w-2xl mx-auto border-border bg-card shadow-lg">
      <CardHeader className="bg-muted/30 border-b pb-8">
        <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
          <CalcIcon className="w-6 h-6 text-primary" />
        </div>
        <CardTitle className="text-2xl">
          Roof Replacement Cost Calculator
        </CardTitle>
        <CardDescription className="text-base">
          Get an instant, rough estimate for your new roof based on Saskatchewan
          market rates. This is for educational purposes only—contact us for a
          firm, fixed-price quote.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-8 pt-8">
        {/* Square Footage */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <Label className="text-base">
              Home Size (Approx. Roof Square Footage)
            </Label>
            <span className="font-bold text-lg">
              {sqft[0].toLocaleString()} sq ft
            </span>
          </div>
          <Slider
            defaultValue={[2000]}
            max={6000}
            min={500}
            step={100}
            value={sqft}
            onValueChange={setSqft}
            className="py-4"
            aria-label="Home Size (Approx. Roof Square Footage)"
          />
          <p className="text-sm text-muted-foreground">
            Tip: Your roof is typically 1.5x to 2x larger than your home's
            footprint.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          {/* Material */}
          <div className="space-y-3">
            <Label htmlFor="material" className="text-base">
              Roofing Material
            </Label>
            <Select value={material} onValueChange={setMaterial}>
              <SelectTrigger
                id="material"
                className="h-12"
                aria-label="Roofing Material"
              >
                <SelectValue placeholder="Select material" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="asphalt">
                  Architectural Asphalt Shingles
                </SelectItem>
                <SelectItem value="metal">Premium Metal Roofing</SelectItem>
                <SelectItem value="flat">
                  Flat / Low Slope (Commercial)
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Pitch */}
          <div className="space-y-3">
            <Label htmlFor="pitch" className="text-base">
              Roof Pitch (Steepness)
            </Label>
            <Select value={pitch} onValueChange={setPitch}>
              <SelectTrigger
                id="pitch"
                className="h-12"
                aria-label="Roof Pitch (Steepness)"
              >
                <SelectValue placeholder="Select pitch" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low Slope (Walkable)</SelectItem>
                <SelectItem value="medium">Medium Slope (Standard)</SelectItem>
                <SelectItem value="steep">
                  Steep Slope (Hard to walk)
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Tear Off */}
        <div className="flex items-center justify-between space-x-2 border rounded-lg p-4 bg-muted/20">
          <div className="space-y-1">
            <Label htmlFor="tear-off" className="text-base">
              Tear-Off Old Roof
            </Label>
            <p className="text-sm text-muted-foreground">
              Include cost to remove and dispose of existing shingles.
            </p>
          </div>
          <Switch
            id="tear-off"
            checked={tearOff}
            onCheckedChange={setTearOff}
            aria-label="Tear-Off Old Roof"
          />
        </div>
      </CardContent>

      <CardFooter className="flex flex-col bg-muted/30 border-t p-8">
        <div className="w-full text-center space-y-2 mb-6">
          <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
            Estimated Cost Range (Not Final)
          </p>
          <div className="text-4xl md:text-5xl font-bold text-primary">
            ${estimate.min.toLocaleString()}{" "}
            <span className="text-muted-foreground text-2xl mx-2">to</span> $
            {estimate.max.toLocaleString()}
          </div>
          <p className="text-sm text-muted-foreground mt-4">
            *This is a rough estimate only and not a final quote. Pitch
            complexity, accessibility, and decking repairs can affect the final
            price.
          </p>
        </div>

        <div className="w-full grid sm:grid-cols-2 gap-4">
          <Button
            size="lg"
            className="w-full h-14 text-lg bg-accent text-accent-foreground hover:bg-accent/90"
            asChild
          >
            <Link to="/contact">Get Exact Quote</Link>
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="w-full h-14 text-lg"
            asChild
          >
            <a href="tel:6397397969">Call to Discuss</a>
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
}
